/**
 * Batch persistence — saves extracted JSON, edits, statuses to IndexedDB
 * keyed by work-folder identity (folder name).
 *
 * The file handles and File objects themselves aren't saved; on session
 * restore we re-pick the work folder and stitch the saved state back
 * onto the live file list by filename.
 */

import { saveBatch, loadBatch, deleteBatch } from './lib/storage.js';
import { normalizeExtraction } from './lib/anthropic.js';
import { getState, setState } from './state.js';

function keyFor(workName) {
  return `wf:${workName || ''}`;
}

export async function saveBatchSnapshot() {
  const s = getState();
  if (!s.workName || !s.files.length) return;
  const payload = {
    savedAt:  new Date().toISOString(),
    workName: s.workName,
    files: s.files.map(f => ({
      name:      f.name,
      size:      f.size,
      status:    f.status,
      pageCount: f.pageCount,
      extracted: f.extracted,
      coding:    f.coding,
      distribution: f.distribution,
      distributionEdits: f.distributionEdits,
      edits:     f.edits,
      error:     f.error,
      ms:        f.ms
      // not persisted: handle, file (FileSystemFileHandle / File), thumbDataUrl
    }))
  };
  try {
    await saveBatch(keyFor(s.workName), payload);
  } catch (err) {
    console.warn('Could not persist batch:', err);
  }
}

/**
 * Stitch a saved batch onto a freshly-enumerated file list. Matches by name.
 */
export async function restoreBatchSnapshot() {
  const s = getState();
  if (!s.workName || !s.files.length) return false;
  let payload;
  try { payload = await loadBatch(keyFor(s.workName)); }
  catch { return false; }
  if (!payload) return false;

  const byName = new Map();
  for (const saved of payload.files || []) byName.set(saved.name, saved);

  let restored = 0;
  for (const f of s.files) {
    const saved = byName.get(f.name);
    if (saved) {
      Object.assign(f, {
        status:    saved.status,
        extracted: saved.extracted ? normalizeExtraction(saved.extracted) : null,
        coding:    saved.coding,
        distribution: saved.distribution || null,
        distributionEdits: saved.distributionEdits || null,
        edits:     saved.edits,
        error:     saved.error,
        ms:        saved.ms
      });
      restored++;
    }
  }
  setState({ files: s.files });
  return restored > 0;
}

export async function clearBatchSnapshot() {
  const s = getState();
  if (!s.workName) return;
  try { await deleteBatch(keyFor(s.workName)); }
  catch { /* ignore */ }
}
